import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { getInhouseAnonymousId } from "src/utils/anonymousId";
import { clearAnonymousThreadId, forgetAnonymousThreadIds } from "src/utils/anonymousThreadId";
import { allReportedIds, mapAnonymousThreads as mapAnonymousThreadsApi } from "src/services/anonymousThreads";
import { toast } from "react-toastify";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";

const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;

export const createUserAPI = createAsyncThunk("onboarding/createUserAPI", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const response = await axios.post(`${BASE_ENDPOINT}/api/user/create`, {}, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const requestLegalReview = createAsyncThunk("onboarding/requestLegalReview", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  var url;
  if (window.location.pathname === "/home") {
    url = `${BASE_ENDPOINT}/api/user/request-lawyer`;
  } else {
    url = `${BASE_ENDPOINT}/api/thread/${data.threadId}/request-legal-review`;
  }
  try {
    // let endpoint = 'request-legal-review'
    const response = await axios.post(
      url,
      {
        description: data.description,
        communication_preference: data.communication_preference,
        phone_number: data.phone_number,
      },
      { headers: headers },
    );
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const markLegalReviewCallScheduled = createAsyncThunk(
  "onboarding/markLegalReviewCallScheduled",
  async (data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axios.post(
        `${BASE_ENDPOINT}/api/thread/${data.threadId}/mark-legal-review-call-scheduled`,
        {},
        { headers: headers },
      );
      return {
        data: data,
        response: response.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error });
    }
  },
);

export const legalReviewButtonClickedIntent = createAsyncThunk(
  "onboarding/legalReviewButtonClickedIntent",
  async (data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      let endpoint = "legal-review-button-clicked";
      const response = await axios.post(
        `${BASE_ENDPOINT}/api/thread/${data.threadId}/${endpoint}`,
        {},
        { headers: headers },
      );
      return {
        data: data,
        response: response.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error });
    }
  },
);

export const getMessageType = createAsyncThunk("onboarding/getMessageType", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const response = await axios.post(
      `${BASE_ENDPOINT}/api/v0/message/get-type/${data.message}`,
      {},
      { headers: headers },
    );
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const addPhoneNumber = createAsyncThunk("onboarding/addPhoneNumber", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const response = await axios.post(
      `${BASE_ENDPOINT}/api/user/phone`,
      {
        phone_number: data.phone_number,
        consent_text: data.consent_text,
        consent_version: data.consent_version,
      },
      { headers: headers },
    );
    return { data: data, response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

/**
 * Predicate the `getUserMetadata` thunk uses to decide whether to fire.
 *
 * Returns false on routes where the viewer has no actionable subscription
 * state and a get-metadata call would just burn requests / thrash
 * userMetadataFetchStatus on focus and cross-tab events:
 *
 *   - `/try`           — anonymous, no BE user record (returns 401/empty).
 *
 * `/chat/shared/*` is intentionally allowed: logged-in users viewing a shared
 * thread need their metadata (subscription_type, etc.) for plan-aware UI.
 * BaseLayout already guards the dispatch behind Clerk's `isSignedIn`.
 *
 * INH-5375 removed the `anonymousThreadId` carve-out that used to let this fire
 * on `/try`: metadata no longer performs the ownership transfer, so there is
 * nothing for such a dispatch to do. `mapAnonymousThreads` carries its own
 * condition for that path instead.
 *
 * Exported for unit testing.
 */
export const shouldFetchUserMetadata = (pathname) => {
  if (typeof pathname !== "string") return true;
  if (pathname.includes("/try")) return false;
  return true;
};

export const getUserMetadata = createAsyncThunk(
  "onboarding/getUserMetadata",
  async (data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    if (!accessToken) {
      return null;
    }
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      // INH-5375: get-metadata is a pure read again. Thread ownership transfer
      // moved to the dedicated mapAnonymousThreads thunk below — mapping is
      // best-effort migration, while this call carries subscription state, and
      // a stale thread ID must never be able to fail it (INH-5217).
      const url = `${BASE_ENDPOINT}/api/user/get-metadata`;
      const response = await axios.get(url, { headers: headers });
      return {
        data: data,
        response: response.data,
      };
    } catch (error) {
      const status = error.response ? error.response.status : null;
      return thunkAPI.rejectWithValue({ error: error, status: status, thunkAPI: thunkAPI });
    }
  },
  {
    condition: () => shouldFetchUserMetadata(window.location.pathname),
  },
);

/**
 * Transfer every anonymous thread this browser knows about to the signed-in
 * user (INH-5375).
 *
 * Deliberately a separate thunk from `getUserMetadata`. Mapping is best-effort
 * migration and is allowed to fail; metadata drives paywalls and checkout and
 * is not. Fusing them is what made one stale thread ID able to blank a user's
 * plan state and their chat history (INH-5217).
 */
export const mapAnonymousThreads = createAsyncThunk(
  "onboarding/mapAnonymousThreads",
  async ({ threadIds }, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    if (!accessToken || !threadIds || threadIds.length === 0) {
      return null;
    }
    try {
      const result = await mapAnonymousThreadsApi(threadIds, accessToken);
      return { threadIds, result };
    } catch (error) {
      const status = error?.response?.status ?? null;
      trackAnalytics(EVENT_NAMES.ANONYMOUS_THREAD_MAPPING_FAILED, {
        [EVENT_PARAMETERS.ATTEMPTED_COUNT]: threadIds?.length ?? 0,
        [EVENT_PARAMETERS.ERROR_STATUS]: status,
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      });
      return thunkAPI.rejectWithValue({ error, threadIds, status });
    }
  },
);

export const getLawyerHub = createAsyncThunk("onboarding/getLawyerHub", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const response = await axios.get(`${BASE_ENDPOINT}/api/user/get-lawyer-hub-data`, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const fetchMeetingDetails = createAsyncThunk("onboarding/fetchMeetingDetails", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  var url = `${BASE_ENDPOINT}/api/user/submit-lawyer-matching-request?event_id=${data.eventId}`;
  if (data.threadId) {
    url += `&thread_id=${data.threadId}`;
  }
  try {
    const response = await axios.get(url, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const storeReferral = createAsyncThunk("onboarding/storeReferral", async (data, thunkAPI) => {
  // const headers = { Authorization: `Bearer ${accessToken}` };
  var headers;
  if (window.location.pathname.includes("/try")) {
    const anonymousId = await getInhouseAnonymousId();
    headers = { "anonymous-user-id": anonymousId };
  } else {
    const accessToken = localStorage.getItem("accessToken2");
    headers = { Authorization: `Bearer ${accessToken}` };
  }

  // const url = `${BASE_ENDPOINT}/api/thread/${data.thread_id}/store-referral-lead`;
  const url = `${BASE_ENDPOINT}/api/store-referral-lead`;
  // Per subscription-tiers-rewrite Group 7 (task 7.8): drop
  // `lawyer_finalization_offer` from the wire payload entirely (omit the key,
  // do NOT send null). Finalization is no longer a separate purchase under the
  // subscription-only model.
  var payload = {
    state: data.state || null,
    name: data.name,
    thread_id: data.thread_id,
    phone: data.phone,
    email: data.email,
    description: data.description,
    referral_category: data.referral_category,
    lawyer_consultation_offer: data.lawyer_consultation_offer,
    type: data.type,
    doc_id: data.doc_id || null,
  };

  if (data?.is_free_consultation_lead) {
    payload.is_free_consultation_lead = data?.is_free_consultation_lead;
  }

  try {
    const response = await axios.post(url, payload, { headers: headers });

    trackAnalytics(EVENT_NAMES.CONSULTATION_BOOKED, {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.CONSULTATION_TYPE]: "plan_included",
      [EVENT_PARAMETERS.THREAD_ID]: data.thread_id,
    });

    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    // Per subscription-tiers-rewrite Group 7 (task 7.12): capture the BE
    // error_code on 402 so the LegalReviewShelf call-site can detect
    // `consultation_credits_exhausted` and run its inline safety-net
    // (dispatch getUserMetadata + broadcast `consultation_consumed`).
    const status = error?.response?.status ?? null;
    const errorCode = error?.response?.data?.detail?.error_code ?? null;
    return thunkAPI.rejectWithValue({ error: error, status, errorCode });
  }
});

export const downgradeSubscription = createAsyncThunk(
  "onboarding/downgradeSubscription",
  async (data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axios.post(
        `${BASE_ENDPOINT}/api/user/downgrade-subscription`,
        {
          subscription_type: data.subscription_type,
          payment_frequency: data.payment_frequency,
          coupon_id: data.coupon_id,
        },
        { headers }
      );
      // Refresh user metadata so the rest of the app reflects the new plan
      thunkAPI.dispatch(getUserMetadata());
      return { data, response: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error });
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  "onboarding/cancelSubscription",
  async (_data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axios.post(
        `${BASE_ENDPOINT}/api/user/cancel-subscription`,
        {},
        { headers }
      );
      thunkAPI.dispatch(getUserMetadata());
      return { response: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error });
    }
  }
);

export const qaCancelSubscription = createAsyncThunk(
  "onboarding/qaCancelSubscription",
  async ({ email }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_ENDPOINT}/qa/cancel-subscription?user_email=${encodeURIComponent(email)}`,
        {},
        { headers: { Authorization: `Bearer ${process.env.REACT_APP_QA_API_KEY}` } }
      );
      thunkAPI.dispatch(getUserMetadata());
      return { response: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error });
    }
  }
);

export const restartSubscription = createAsyncThunk(
  "onboarding/restartSubscription",
  async (_data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axios.post(
        `${BASE_ENDPOINT}/api/user/restart-subscription`,
        {},
        { headers }
      );
      thunkAPI.dispatch(getUserMetadata());
      return { response: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error });
    }
  }
);

export const getBillingHistory = createAsyncThunk(
  "onboarding/getBillingHistory",
  async (_data, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axios.get(
        `${BASE_ENDPOINT}/api/user/billing-history`,
        { headers }
      );
      return { response: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error });
    }
  }
);

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState: {
    clerkLocalization: {
      signUp: {
        start: {
          title: "",
          subtitle: "",
          buttonText: "Sign Up",
        },
        continue: {
          title: "Sign Up",
          actionText: "Sign Up",
        },
      },
      signIn: {
        start: {
          title: "",
          // subtitle: "Welcome back! Please enter your details",
        },
      },
    },
    titleContext: "", // Add new field to track context for title customization
    upgradeModalData: {},
    showUpgradeModalTryPage: false,
    status: "idle",
    error: "",
    userIdentified: false,
    reTriggerMetadata: false,
    createUserAPIStatus: "idle",
    meetingDetails: {},
    userConfig: {},
    userMetadata: {},
    markLegalReviewCallScheduledStatus: "idle",
    markLegalReviewCallScheduledError: "",
    userMetadataFetchStatus: "idle",
    // Anonymous-thread mapping tracking (INH-5375). One call now covers every
    // stored thread ID, so the gate asks "has the mapping covering thread X
    // settled?" rather than tracking a single remap target. Kept separate from
    // `userMetadataFetchStatus`, which every routine metadata refresh shares.
    anonymousMapStatus: "idle",
    anonymousMapFailed: false,
    // The IDs the in-flight/last call covered, so consumers can ask "was THIS
    // thread part of the mapping?" — the question the old singular
    // anonymousRemapThreadId answered.
    anonymousMapAttemptedIds: [],
    anonymousMappedIds: [],
    anonymousUnmappedIds: [],
    userAlreadyExist: false,
    serverError: null,
    showTryAgainPage: false,
    plansToShow: [],
    lawyerHubData: [],
    openSinglePlanModal: {
      show: false,
      planType: "",
    },
    openMultiPlanModal: {
      show: false,
      planTypes: [],
    },

    requestLegalReviewAsideDetails: {
      show: false,
      isLoadingHandoffContext: false,
      fetchedHandoffContext: null,
    },

    referralCategory: "employment",
    referralCategoryText: "Talk to employment lawyer",
    storeReferralStatus: "idle",
    // Per subscription-tiers-rewrite Group 7 (task 7.12): BE error code from
    // the last `storeReferral` rejection (e.g. `consultation_credits_exhausted`).
    storeReferralErrorCode: null,
    showUpgradePlanPopupChatPage: false,
    showSubscriptionPopup: false,
    showUpgradingPlan: false,
    showBusinessPlanUnlocked: false,
    showFindBestLawyer: false,
    showMeetingConfirmation: false,
    showNextSteps: false,
    openSignupPopup: false,
    showScheduleMeeting: false,
    showReviewRequestedModal: false,
    requestLegalReviewStatus: "idle",
    requestLegalReviewFailureReason: "",
    showCheckoutModal: false,
    currentPageLegalReviewStatus: {},
    showLearnMoreModal: false,
    showLegalReviewRequestStatus: false,
    storeReferalEmail: null,
    showLegalReviewCompletedModal: false,
    showPersonalPlanThreadsExhausted: false,
    showLawyerCard: false,
    showLawyerAssignedModal: false,
    showOngoingReviewModal: false,
    showReviewCompleteModal: false,
    lawyerCalendlyLink: null,
    documentNameShortHand: "",
    showCallScheduledPopup: false,
    intent: "",
    showPersonalizePopup: false,
    signupPopupSlide: "signup",
    isYouareAllset: false,
    addPhoneStatus: "idle",
    downgradeSubscriptionStatus: "idle",
    downgradeSubscriptionError: null,
    downgradeEffectiveAt: null,
    cancelSubscriptionStatus: "idle",
    cancelSubscriptionError: null,
    cancelEffectiveAt: null,
    qaCancelSubscriptionStatus: "idle",
    qaCancelSubscriptionError: null,
    restartSubscriptionStatus: "idle",
    billingHistory: [],
    billingHistoryStatus: "idle",
  },
  reducers: {
    // Payment plan showcase modals
    setSinglePlanModal: (state, action) => {
      // action.payload = { show: true/false, planType: "pro/consultation/counsel" }
      state.openSinglePlanModal = action.payload;
    },
    setMultiPlanModal: (state, action) => {
      // action.payload = { show: true/false, planTypes: ["pro", "consultation", "counsel"] }
      state.openMultiPlanModal = action.payload;
    },

    updateClerkLocalization: (state, action) => {
      state.clerkLocalization = { ...state.clerkLocalization, ...action.payload };
    },
    setShowCallScheduledPopup: (state, action) => {
      state.showCallScheduledPopup = action.payload;
    },
    setDocumentNameShortHand: (state, action) => {
      state.documentNameShortHand = action.payload;
    },
    setUserConfig: (state, action) => {
      state.userConfig = action.payload;
    },
    setShowLegalReviewRequestStatus: (state, action) => {
      state.showLegalReviewRequestStatus = action.payload;
    },
    setShowLegalReviewCompletedModal: (state, action) => {
      state.showLegalReviewCompletedModal = action.payload;
    },
    resetRequestLegalReviewFailureReason: (state) => {
      state.requestLegalReviewFailureReason = "";
      state.requestLegalReviewStatus = "idle";
    },
    setCurrentPageLegalReviewStatus: (state, action) => {
      state.currentPageLegalReviewStatus = action.payload;
    },
    setShowLawyerCard: (state, action) => {
      state.showLawyerCard = action.payload;
    },
    setShowScheduleMeeting: (state, action) => {
      state.showScheduleMeeting = action.payload.show;
      state.lawyerCalendlyLink = action.payload.lawyer_details?.calendly_link;
    },
    setShowCheckoutModal: (state, action) => {
      state.showCheckoutModal = action.payload;
    },
    setShowReviewRequestedModal: (state, action) => {
      state.showReviewRequestedModal = action.payload;
    },
    setPricingModalData: (state, action) => {
      state.plansToShow = action.payload.plans;
    },

    resetStoreReferralStatus: (state) => {
      state.storeReferralStatus = "idle";
    },
    setRequestLegalReviewAsideDetails: (state, action) => {
      const isClosing = action.payload.show === false;
      state.requestLegalReviewAsideDetails = {
        show: action.payload.show,
        referralCategory: action.payload.referralCategory,
        referralCategoryText: action.payload.referralCategoryText,
        threadId: action.payload.threadId,
        offer: action.payload.offer,
        btn: action.payload.btn,
        isLoadingHandoffContext: isClosing ? false : (action.payload.isLoadingHandoffContext ?? false),
        fetchedHandoffContext: isClosing ? null : (action.payload.fetchedHandoffContext ?? null),
      };
    },
    setHandoffContext: (state, action) => {
      state.requestLegalReviewAsideDetails.fetchedHandoffContext = action.payload;
      state.requestLegalReviewAsideDetails.isLoadingHandoffContext = false;
    },
    setShowSignupToUpgradeModal: (state, action) => {
      state.openSignupPopup = action.payload.show;
      state.plansToShow = action.payload.plans;
      state.intent = action.payload.intent;
      if (action.payload.intent === "inhouse_pro") {
        state.clerkLocalization = {
          ...state.clerkLocalization,
          signUp: {
            ...state.clerkLocalization.signUp,
            start: {
              ...state.clerkLocalization.signUp.start,
              title: "Sign up to access your document and review it with a lawyer",
            },
          },
        };
      }
      if (action.payload.intent === "inhouse_counsel") {
        state.clerkLocalization = {
          ...state.clerkLocalization,
          signUp: {
            start: {
              ...state.clerkLocalization.signUp.start,
              title: "Sign up to continue",
            },
          },
        };
      }
      state.signupPopupSlide = action.payload.slide || "signup";
    },
    setSignupPopupSlide: (state, action) => {
      state.signupPopupSlide = action.payload;
    },
    setShowUpgradePlanPopupChatPage: (state, action) => {
      state.showUpgradePlanPopupChatPage = action.payload.show;
      state.plansToShow = action.payload.plans;
    },
    setPlansToShow: (state, action) => {
      state.plansToShow = action.payload;
    },
    setShowSubscriptionPopup: (state, action) => {
      state.showSubscriptionPopup = action.payload;
    },
    setShowUpgradingPlan: (state, action) => {
      state.showUpgradingPlan = action.payload;
    },
    setShowBusinessPlanUnlocked: (state, action) => {
      state.showBusinessPlanUnlocked = action.payload;
    },
    setShowFindBestLawyer: (state, action) => {
      state.showFindBestLawyer = action.payload;
    },
    setShowPersonalPlanThreadsExhausted: (state, action) => {
      state.showPersonalPlanThreadsExhausted = action.payload;
    },
    setShowMeetingConfirmation: (state, action) => {
      state.showMeetingConfirmation = action.payload;
    },
    setShowLearnMoreModal: (state, action) => {
      // remove this in future. 7/11/2025
      state.showLearnMoreModal = action.payload;
      state.requestLegalReviewAsideDetails = { ...state.requestLegalReviewAsideDetails, show: false };
    },
    decreaseThreadsRemaining: (state) => {
      const current = state.userMetadata?.free_threads_remaining;
      if (typeof current === "number" && current > 0) {
        state.userMetadata = { ...state.userMetadata, free_threads_remaining: current - 1 };
      }
    },
    decreaseReviewRemaining: (state) => {
      state.userMetadata = {
        ...state.userMetadata,
        legal_review_credits_used: state.userMetadata.legal_review_credits_used + 1,
      };
    },
    setReTriggerMetadata: (state, action) => {
      state.reTriggerMetadata = action.payload;
    },
    setShowLawyerAssignedModal: (state, action) => {
      state.showLawyerAssignedModal = action.payload;
    },
    setShowOngoingReviewModal: (state, action) => {
      state.showOngoingReviewModal = action.payload;
    },
    setShowReviewCompleteModal: (state, action) => {
      state.showReviewCompleteModal = action.payload;
    },
    setUserIdentified: (state, action) => {
      state.userIdentified = action.payload;
    },
    setUpgradeModalData: (state, action) => {
      state.showUpgradeModalTryPage = action.payload.show;
      state.upgradeModalData = action.payload.data;
    },
    setShowPersonalizePopup: (state, action) => {
      state.showPersonalizePopup = action.payload;
    },
    setShowYouareAllset: (state, action) => {
      state.isYouareAllset = action.payload;
    },
    updateLawyerHubData: (state, action) => {
      if (state.lawyerHubData) {
        state.lawyerHubData = [...state.lawyerHubData, action.payload];
      } else {
        state.lawyerHubData = [action.payload];
      }
    },
    resetDowngradeSubscriptionStatus: (state) => {
      state.downgradeSubscriptionStatus = "idle";
      state.downgradeSubscriptionError = null;
      state.downgradeEffectiveAt = null;
    },
    resetCancelSubscriptionStatus: (state) => {
      state.cancelSubscriptionStatus = "idle";
      state.cancelSubscriptionError = null;
      state.cancelEffectiveAt = null;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(createUserAPI.pending, (state) => {
        state.createUserAPIStatus = "loading";
      })
      .addCase(createUserAPI.fulfilled, (state, action) => {
        state.createUserAPIStatus = "success";
      })
      .addCase(createUserAPI.rejected, (state, action) => {
        state.createUserAPIStatus = "failed";
        var response = action.payload?.error?.request?.response;
        if (action?.payload?.error?.response?.status === 409) {
          state.userAlreadyExist = true;
        } else if (response && response.indexOf("User already exists with email") > -1) {
          state.userAlreadyExist = true;
        }

        ErrorLoggingV2(action?.payload?.error, "create_user_api_error");
      })
      // Anonymous-thread mapping (INH-5375). Kept entirely separate from the
      // metadata cases above: a failure here must never touch
      // userMetadataFetchStatus, or we recreate the coupling this change exists
      // to remove.
      .addCase(mapAnonymousThreads.pending, (state, action) => {
        state.anonymousMapStatus = "pending";
        state.anonymousMapFailed = false;
        state.anonymousMapAttemptedIds = action.meta?.arg?.threadIds || [];
      })
      .addCase(mapAnonymousThreads.fulfilled, (state, action) => {
        state.anonymousMapStatus = "settled";
        if (!action.payload) return;
        const { result } = action.payload;
        state.anonymousMappedIds = [...result.success, ...result.already_mapped];
        state.anonymousUnmappedIds = [...result.belongs_to_another_user, ...result.invalid_thread_id];
        // All four buckets are terminal, so anything the server named is done
        // with. IDs it never mentioned stay put and retry on the next login.
        forgetAnonymousThreadIds(allReportedIds(result));
      })
      .addCase(mapAnonymousThreads.rejected, (state, action) => {
        // The request itself failed — network error, 4xx or 5xx. This is the
        // ONLY case the user is told about: per-ID outcomes on a 200 are
        // ordinary migration bookkeeping and mean nothing to them, but a failed
        // request means chats they can see today may be missing after this.
        state.anonymousMapStatus = "settled";
        // Retain every stored ID: with no per-ID verdict, nothing is safe to
        // delete, so the next login retries the whole set.
        state.anonymousMapFailed = true;

        toast.error("We couldn't link your earlier chats to your account. Please refresh to try again.", {
          // Stable id — PostAuth and the ownership gate can both dispatch, and
          // the user should see one message, not one per attempt.
          toastId: "anonymous-thread-mapping-failed",
        });
      })
      // getUsermetadata api : this creates new user on the backend
      .addCase(getUserMetadata.pending, (state) => {
        state.userMetadataFetchStatus = "loading";
        state.serverError = null;
        state.showTryAgainPage = false;
      })
      .addCase(getUserMetadata.fulfilled, (state, action) => {
        state.userMetadataFetchStatus = "success";
        // action.payload is null when there is no accessToken (see thunk above) —
        // keep whatever metadata already exists instead of overwriting it.
        if (!action.payload) return;
        state.userMetadata = action.payload.response;
      })
      .addCase(getUserMetadata.rejected, (state, action) => {
        // INH-4514: If metadata was already fetched successfully (e.g. a
        // cached-on-FE refresh triggered by a button), a subsequent failed
        // get-metadata call must not degrade the working page. Treat it as a
        // no-op: keep the existing metadata and report success, without
        // flipping to any failure status or firing error side-effects.
        if (Object.keys(state.userMetadata || {}).length > 0) {
          state.userMetadataFetchStatus = "success";
          return;
        }
        // INH-5375 removed the AnonymousThread* / AnonymousID* branches and the
        // 422 branch that reloaded the page via window.location.href. None of
        // them can fire now that get-metadata no longer carries the
        // anonymous_thread_id parameter — those failures belong to
        // mapAnonymousThreads, which reports them per-ID without failing.
        if (action?.payload?.status === 401) {
          state.userMetadataFetchStatus = "token-failure";
        } else if (action?.payload?.status === 404 || action?.payload?.status === 400) {
          if (action?.payload?.error?.response?.data?.detail?.error?.search("UserNotFoundException") > -1) {
            state.userMetadataFetchStatus = "profile-not-created";
          } else if (
            action?.payload?.error?.response?.data?.detail?.error?.search("OnboardingIncompleteException") > -1
          ) {
            state.userMetadataFetchStatus = "profile-not-completed";
          } else {
            state.userMetadataFetchStatus = "failed";
          }
        } else if (action?.payload?.status > 499) {
          state.serverError = action.payload.error;
          state.showTryAgainPage = true;
          state.userMetadata = {
            email: action?.payload?.error?.response?.data?.detail?.email,
            user_id: action?.payload?.error?.response?.data?.detail?.user,
          };
        } else {
          state.userMetadataFetchStatus = "failed";
        }
        if (action.payload?.error?.response?.status !== 401) {
          // Enhanced error logging with more context
          const errorDetails = {
            error: action?.payload?.error,
            status: action?.payload?.status,
            endpoint: "get-metadata",
            responseData: action?.payload?.error?.response?.data,
            message: action?.payload?.error?.message,
            userMetadataFetchStatus: state.userMetadataFetchStatus,
            timestamp: new Date().toISOString(),
          };
          ErrorLoggingV2(errorDetails, "get_user_metadata_error");
        }
      })

      .addCase(getLawyerHub.pending, (state) => {
        state.lawyerHubFetchStatus = "loading";
      })
      .addCase(getLawyerHub.fulfilled, (state, action) => {
        state.lawyerHubData = action.payload.response;
        state.lawyerHubFetchStatus = "success";
      })
      .addCase(getLawyerHub.rejected, (state, action) => {
        state.lawyerHubFetchStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "get_lawyer_hub_error");
        }
      })

      .addCase(markLegalReviewCallScheduled.pending, (state) => {
        state.markLegalReviewCallScheduledStatus = "loading";
        state.markLegalReviewCallScheduledError = "";
      })
      .addCase(markLegalReviewCallScheduled.fulfilled, (state, action) => {
        state.markLegalReviewCallScheduledStatus = "success";
        state.markLegalReviewCallScheduledError = "";
      })
      .addCase(markLegalReviewCallScheduled.rejected, (state, action) => {
        state.markLegalReviewCallScheduledStatus = "failed";
        state.markLegalReviewCallScheduledError = action.payload?.error;
        ErrorLoggingV2(action?.payload?.error, "mark_legal_review_call_scheduled_error");
      })

      .addCase(getMessageType.pending, (state) => {
        state.getMessageTypeStatus = "loading";
        state.getMessageTypeError = "";
      })
      .addCase(getMessageType.fulfilled, (state, action) => {
        state.getMessageTypeStatus = "success";
        state.getMessageTypeError = "";
        state.messageType = action.payload.response.message_type;
        localStorage.setItem("messageType", action.payload.response.message_type);
      })
      .addCase(getMessageType.rejected, (state, action) => {
        state.getMessageTypeStatus = "failed";
        state.getMessageTypeError = action.payload?.error;
        localStorage.setItem("messageType", "ask");
        ErrorLoggingV2(action?.payload?.error, "get_message_type_error");
      })

      .addCase(requestLegalReview.pending, (state) => {
        state.requestLegalReviewStatus = "loading";
        state.requestLegalReviewFailureReason = "";
      })
      .addCase(requestLegalReview.fulfilled, (state, action) => {
        // if (action.payload.response.is_accepted) {
        // var legalCreditsAvailable = state.userMetadata.legal_review_credits_total - state.userMetadata.legal_review_credits_used - 1;
        state.requestLegalReviewStatus = "success";
      })
      .addCase(requestLegalReview.rejected, (state, action) => {
        if (action?.payload?.error?.request?.response) {
          state.requestLegalReviewFailureReason = JSON.parse(action?.payload?.error?.request?.response || "{}");
        }
        state.requestLegalReviewStatus = "failed";
        ErrorLoggingV2(action?.payload?.error, "request_legal_review_error");
      })

      .addCase(fetchMeetingDetails.pending, (state) => {
        state.fetchMeetingDetailsStatus = "loading";
      })
      .addCase(fetchMeetingDetails.fulfilled, (state, action) => {
        state.fetchMeetingDetailsStatus = "success";
        state.meetingDetails = action.payload.response;
      })
      .addCase(fetchMeetingDetails.rejected, (state, action) => {
        state.fetchMeetingDetailsStatus = "failed";
        ErrorLoggingV2(action?.payload?.error, "fetch_meeting_details_error");
      })
      .addCase(storeReferral.pending, (state, action) => {
        state.storeReferralStatus = "loading";
        state.storeReferralErrorCode = null;
      })
      .addCase(storeReferral.fulfilled, (state, action) => {
        state.storeReferralStatus = "success";
        state.storeReferalEmail = action.payload.data.email;
        state.storeReferralErrorCode = null;
      })
      .addCase(storeReferral.rejected, (state, action) => {
        state.storeReferralStatus = "failed";
        state.storeReferralErrorCode = action?.payload?.errorCode ?? null;
        ErrorLoggingV2(action?.payload?.error, "store_referral_error");
      });

    builder
      .addCase(addPhoneNumber.pending, (state) => {
        state.addPhoneStatus = "loading";
      })
      .addCase(addPhoneNumber.fulfilled, (state, action) => {
        state.addPhoneStatus = "success";
        if (state.userMetadata) {
          state.userMetadata.phone_number = action.meta.arg.phone_number;
        }
      })
      .addCase(addPhoneNumber.rejected, (state, action) => {
        state.addPhoneStatus = "error";
        ErrorLoggingV2(action?.payload?.error, "add_phone_error");
      });

    builder
      .addCase(downgradeSubscription.pending, (state) => {
        state.downgradeSubscriptionStatus = "loading";
        state.downgradeSubscriptionError = null;
      })
      .addCase(downgradeSubscription.fulfilled, (state, action) => {
        state.downgradeSubscriptionStatus = "success";
        state.downgradeEffectiveAt = action.payload?.response?.effective_at ?? null;
      })
      .addCase(downgradeSubscription.rejected, (state, action) => {
        state.downgradeSubscriptionStatus = "failed";
        // BE 400 returns `detail` as a plain string (business-rule rejections);
        // BE 422 returns Pydantic-shape `detail: [{ msg, loc, type }]`. Prefer
        // the string form so the user sees the actual BE message instead of
        // the generic fallback.
        const detail = action?.payload?.error?.response?.data?.detail;
        state.downgradeSubscriptionError =
          (typeof detail === "string" ? detail : null) ||
          detail?.[0]?.msg ||
          "Something went wrong. Please try again.";
        ErrorLoggingV2(action?.payload?.error, "downgrade_subscription_error");
      });

    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.cancelSubscriptionStatus = "loading";
        state.cancelSubscriptionError = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.cancelSubscriptionStatus = "success";
        state.cancelEffectiveAt = action.payload?.response?.effective_at ?? null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.cancelSubscriptionStatus = "failed";
        // See downgradeSubscription.rejected for the string-vs-array rationale.
        const detail = action?.payload?.error?.response?.data?.detail;
        state.cancelSubscriptionError =
          (typeof detail === "string" ? detail : null) ||
          detail?.[0]?.msg ||
          "Something went wrong. Please try again.";
        ErrorLoggingV2(action?.payload?.error, "cancel_subscription_error");
      });

    builder
      .addCase(qaCancelSubscription.pending, (state) => {
        state.qaCancelSubscriptionStatus = "loading";
        state.qaCancelSubscriptionError = null;
      })
      .addCase(qaCancelSubscription.fulfilled, (state) => {
        state.qaCancelSubscriptionStatus = "success";
      })
      .addCase(qaCancelSubscription.rejected, (state, action) => {
        state.qaCancelSubscriptionStatus = "failed";
        const detail = action?.payload?.error?.response?.data?.detail;
        state.qaCancelSubscriptionError =
          (typeof detail === "string" ? detail : null) ||
          detail?.[0]?.msg ||
          "Something went wrong. Please try again.";
      });

    builder
      .addCase(getBillingHistory.pending, (state) => {
        state.billingHistoryStatus = "loading";
      })
      .addCase(getBillingHistory.fulfilled, (state, action) => {
        state.billingHistoryStatus = "success";
        state.billingHistory = action.payload?.response ?? [];
      })
      .addCase(getBillingHistory.rejected, (state, action) => {
        state.billingHistoryStatus = "failed";
        ErrorLoggingV2(action?.payload?.error, "get_billing_history_error");
      });

    builder
      .addCase(restartSubscription.pending, (state) => {
        state.restartSubscriptionStatus = "loading";
      })
      .addCase(restartSubscription.fulfilled, (state) => {
        state.restartSubscriptionStatus = "success";
      })
      .addCase(restartSubscription.rejected, (state, action) => {
        state.restartSubscriptionStatus = "failed";
        ErrorLoggingV2(action?.payload?.error, "restart_subscription_error");
      });
  },
});

export default onboardingSlice.reducer;

export const {
  setSinglePlanModal,
  setMultiPlanModal,

  setPricingModalData,
  setRequestLegalReviewAsideDetails,
  setHandoffContext,
  setShowUpgradePlanPopupChatPage,
  setShowSubscriptionPopup,
  setShowUpgradingPlan,
  setShowBusinessPlanUnlocked,
  setShowFindBestLawyer,
  setShowMeetingConfirmation,
  setShowReviewRequestedModal,
  setShowCheckoutModal,
  setCurrentPageLegalReviewStatus,
  setShowScheduleMeeting,
  decreaseThreadsRemaining,
  decreaseReviewRemaining,
  setShowLearnMoreModal,
  resetRequestLegalReviewFailureReason,
  setShowLegalReviewRequestStatus,
  setShowLegalReviewCompletedModal,
  setShowPersonalPlanThreadsExhausted,
  setShowLawyerCard,
  setShowLawyerAssignedModal,
  setShowOngoingReviewModal,
  setShowReviewCompleteModal,
  setUserIdentified,
  setUserConfig,
  setDocumentNameShortHand,
  setShowCallScheduledPopup,
  setUpgradeModalData,
  setShowSignupToUpgradeModal,
  updateClerkLocalization,
  resetStoreReferralStatus,
  setShowPersonalizePopup,
  setSignupPopupSlide,
  setPlansToShow,
  setShowYouareAllset,
  updateLawyerHubData,
  setReTriggerMetadata,
  resetDowngradeSubscriptionStatus,
  resetCancelSubscriptionStatus,
} = onboardingSlice.actions;
